import { WebsiteConfiguration } from '@cineamo/api/models/website-configuration/WebsiteConfiguration.types';
import { WebsiteStyle } from '@cineamo/api/models/website-configuration/WebsiteStyle.types';

import { isThemeLight, isValidHexColor } from '@cineamo/ui/src/helpers/color-helper';

/** The visitor's explicit choice. `auto` (or no cookie) follows the system preference. */
export type ColorThemePreference = 'light' | 'dark' | 'auto';

export const COLOR_THEME_PREFERENCES: ColorThemePreference[] = ['auto', 'light', 'dark'];

export type ColorTheme = 'light' | 'dark';

/** Cookie holding the visitor's explicit choice (`light` | `dark` | `auto`). */
export const COOKIE_COLOR_THEME = 'color-theme';

/**
 * Cookie holding the system preference (`light` | `dark`), kept up to date by
 * public/color-theme-sync.js (loaded by BaseLayout) so the server can resolve `auto`.
 */
export const COOKIE_SYSTEM_COLOR_THEME = 'system-color-theme';

/** Persists the visitor's choice (client-side) so the server can resolve it on the next render. */
export function setColorThemePreferenceCookie(preference: ColorThemePreference): void {
    document.cookie = `${COOKIE_COLOR_THEME}=${preference};path=/;max-age=31536000;SameSite=Lax`;
}

export function parseColorThemePreference(value: string | undefined): ColorThemePreference {
    return value === 'light' || value === 'dark' ? value : 'auto';
}

export function hasDarkStyle(websiteConfiguration: WebsiteConfiguration | undefined): boolean {
    return Object.keys(websiteConfiguration?.darkStyle ?? {}).length > 0;
}

export function resolveColorTheme(
    preference: ColorThemePreference,
    systemColorTheme: string | undefined,
    isDarkStyleConfigured: boolean
): ColorTheme {
    if (!isDarkStyleConfigured) {
        return 'light';
    }
    if (preference === 'auto') {
        return systemColorTheme === 'dark' ? 'dark' : 'light';
    }
    return preference;
}

/**
 * The colour scheme the *browser* should use for everything it paints itself rather than us: the
 * canvas behind the document, the overscroll area, native controls and scrollbars — and, on iOS 26,
 * the translucent material of the top bar. Safari derives that material's light/dark appearance
 * from the root element's colour scheme, and with nothing declared it assumes light, which is why
 * a dark cinema got a white bar.
 *
 * Derived from the palette, not from `data-color-theme`: that attribute says which of the two
 * configured styles is active, not whether it is dark. A cinema without a `darkStyle` is always
 * `light` there, even with a background of `#042968`. The comparison against `onBackgroundColor`
 * reuses the existing theme detection instead of inventing a luminance threshold.
 *
 * A single keyword on purpose. `light dark` would defer to the visitor's device and hand a
 * dark-configured cinema a white canvas in light mode; `only light` additionally keeps Chrome's
 * auto dark theme from inverting a light cinema.
 */
export function resolveUaColorScheme(style: WebsiteStyle | undefined): 'dark' | 'only light' {
    const background = style?.backgroundColor;
    const onBackground = style?.onBackgroundColor;
    if (!background || !isValidHexColor(background) || !onBackground || !isValidHexColor(onBackground)) {
        return 'only light';
    }
    return isThemeLight(background, onBackground) ? 'only light' : 'dark';
}
